import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartCtx = createContext();
const KEY = "onelook_cart_v2";

// Only pieces with a non-empty name are considered real
const realPieces = (look) => (look?.pieces || []).filter(p => (p?.name || "").trim());

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  // selections: array of { piece_id, piece_type, piece_name, sizes, colors, size, color }
  const addComplete = (look, selections) => {
    const line = {
      id: crypto.randomUUID(),
      kind: "complete",
      look_id: look.id,
      look_number: look.number,
      look_name: look.name,
      image: look.hero_image,
      quantity: 1,
      price: look.bundle_price,
      selections: selections.map(s => ({
        piece_id: s.piece_id,
        piece_type: s.piece_type,
        piece_name: s.piece_name,
        sizes: s.sizes || [],
        colors: s.colors || [],
        size: s.size || "",
        color: s.color || "",
      })),
    };
    setItems(prev => [...prev, line]);
    setOpen(true);
  };

  const addPiece = (look, piece, size, color) => {
    const line = {
      id: crypto.randomUUID(),
      kind: "piece",
      look_id: look.id,
      look_number: look.number,
      look_name: look.name,
      piece_id: piece.id,
      piece_name: piece.name,
      piece_type: piece.type,
      image: piece.image || look.hero_image,
      sizes: piece.sizes || [],
      colors: piece.colors || [],
      size, color: color || "",
      quantity: 1, price: piece.price,
    };
    setItems(prev => [...prev, line]);
    setOpen(true);
  };

  const remove = (id) => setItems(prev => prev.filter(i => i.id !== id));
  const updateQty = (id, q) => setItems(prev => prev.map(i => i.id === id ? { ...i, quantity: Math.max(1, q) } : i));
  const updateSize = (id, patch) => setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));
  // Update one piece's size/color within a line's selections
  const updateSelection = (id, piece_id, patch) => setItems(prev => prev.map(i => {
    if (i.id !== id || !i.selections) return i;
    return { ...i, selections: i.selections.map(s => s.piece_id === piece_id ? { ...s, ...patch } : s) };
  }));
  const clear = () => setItems([]);

  const total = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  return (
    <CartCtx.Provider value={{ items, open, setOpen, addComplete, addPiece, remove, updateQty, updateSize, updateSelection, clear, total, count, realPieces }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
export { realPieces };
